/**
 * One tenant: what it is, what it is entitled to, and the invitation it is
 * waiting on.
 *
 * @module components/platform/TenantDetail
 */

import React, { useEffect, useRef, useState } from "react";

import { ReissueInvitationForm } from "@/components/platform/ReissueInvitationForm";
import { tenantState } from "@/components/platform/tenant-directory";
import type { ReissueDraft } from "@/components/platform/useTenantDirectory";
import {
  Button,
  EmptyState,
  ErrorAlert,
  Heading,
  Icon,
  KeyValue,
  MutedText,
  Panel,
  Spinner,
  StatusBadge,
  Time,
  Toggle,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { TenantOnboardingSummary } from "@/platform";

export interface TenantDetailProps {
  tenant: TenantOnboardingSummary | null;
  /** True while the directory is still being read; nothing is selectable yet. */
  loading: boolean;
  entitlementSaving: boolean;
  entitlementError: string | null;
  onEntitlementChange: (tenantId: string, enabled: boolean) => void;
  reissuing: boolean;
  reissueError: string | null;
  onReissue: (tenantId: string, draft: ReissueDraft) => Promise<boolean>;
  onReissueClosed: () => void;
  /** The directory could not be read, so no tenant is known to exist or not. */
  unreadable?: boolean;
}

function detail(tenant: TenantOnboardingSummary): React.ReactElement {
  return (
    <KeyValue
      entries={[
        {
          label: "Directory",
          value: (
            <span className="inline-flex items-center gap-1">
              <Icon name={tenant.entraTenantBound ? "link" : "link-break"} aria-hidden />
              {tenant.entraTenantBound ? "Linked" : "Not linked yet"}
            </span>
          ),
        },
        {
          label: "Nominated admin",
          value: (
            <span
              className="inline-flex items-center gap-1 break-all"
              data-testid={
                tenant.nominatedAdminEmail ? `platform-console-nominated-${tenant.id}` : undefined
              }
            >
              <Icon name="envelope-simple" aria-hidden />
              {tenant.nominatedAdminEmail ?? "None outstanding"}
            </span>
          ),
        },
        {
          label: "Created",
          value: (
            <span className="inline-flex items-center gap-1">
              <Icon name="calendar" aria-hidden />
              <Time value={tenant.createdAt} variant="date" />
            </span>
          ),
        },
      ]}
    />
  );
}

/**
 * @example
 * ```tsx
 * <TenantDetail tenant={selected} loading={loading} … />
 * ```
 */
export function TenantDetail({
  tenant,
  loading,
  entitlementSaving,
  entitlementError,
  onEntitlementChange,
  reissuing,
  reissueError,
  onReissue,
  onReissueClosed,
  unreadable = false,
}: TenantDetailProps): React.ReactElement {
  // Keyed to the tenant it was opened for, not a bare boolean, so moving the
  // selection closes the form rather than re-pointing it at somebody else's
  // invitation. Clearing it from an effect instead raced the click.
  const [reissuingTenantId, setReissuingTenantId] = useState<string | null>(null);
  // The form replaces the button, so closing it has to hand focus back the way
  // opening it hands focus forward. Written in the close handler rather than
  // an effect: the button only exists once the render below has run.
  const restoreFocusTo = useRef<string | null>(null);
  useEffect(() => {
    const id = restoreFocusTo.current;
    // Held until the render that closes the form: another update can render
    // first, with the form still up and no button to focus.
    if (id && reissuingTenantId === null) {
      restoreFocusTo.current = null;
      document.querySelector<HTMLElement>(`[data-testid="${id}"]`)?.focus();
    }
  });

  let body: React.ReactNode;
  if (loading) {
    body = <ListBones rows={3} label="Loading tenant details" />;
  } else if (!tenant && unreadable) {
    body = (
      <EmptyState
        icon="buildings"
        title="The tenant's details could not be loaded"
        description="Use the alert above to load the directory again."
        density="row"
        data-testid="platform-console-tenant-unreadable"
      />
    );
  } else if (!tenant) {
    body = (
      <EmptyState
        icon="buildings"
        title="Select a tenant to see its details"
        density="row"
        data-testid="platform-console-no-tenant-selected"
      />
    );
  } else {
    const state = tenantState(tenant);
    body = (
      <>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Heading level={3} size="lg">
              {tenant.name}
            </Heading>
            {tenant.slug ? <MutedText size="caption">{tenant.slug}</MutedText> : null}
          </div>
          <span className="flex flex-wrap gap-1.5" data-testid="platform-console-detail-state">
            <StatusBadge status={state.status} label={state.label} />
            {state.alsoLabel ? (
              <StatusBadge status={tenant.onboardingStatus} label={state.alsoLabel} />
            ) : null}
          </span>
        </div>

        <div className="mt-4">{detail(tenant)}</div>

        {tenant.invitationExpiresAt ? (
          <MutedText
            size="caption"
            className="mt-3 inline-flex items-center gap-1"
            data-testid={`platform-console-invitation-expires-${tenant.id}`}
          >
            <Icon name="clock-countdown" aria-hidden />
            Invitation expires <Time value={tenant.invitationExpiresAt} variant="date" />
          </MutedText>
        ) : null}

        <Panel
          tone="inset"
          headingLevel={3}
          icon="broadcast"
          title="Public curriculum API"
          description="Allow this organisation to publish approved programmes through its anonymous portfolio API."
          className="mt-4"
          actions={
            <label className="flex items-center gap-3" htmlFor={`public-api-${tenant.id}`}>
              <span className="text-md font-medium">
                Tenant access: {tenant.publicCurriculumApiEnabled ? "Enabled" : "Disabled"}
              </span>
              <Toggle
                id={`public-api-${tenant.id}`}
                checked={tenant.publicCurriculumApiEnabled}
                disabled={entitlementSaving}
                disabledReason={
                  entitlementSaving ? "Wait for the change in progress to finish." : undefined
                }
                onChange={(enabled) => {
                  onEntitlementChange(tenant.id, enabled);
                }}
                aria-label={`Public curriculum API for ${tenant.name}`}
                data-testid={`platform-console-public-api-${tenant.id}`}
              />
            </label>
          }
        >
          {entitlementSaving || entitlementError ? (
            <div className="space-y-3">
              {entitlementSaving ? (
                <MutedText
                  size="caption"
                  className="inline-flex items-center gap-2"
                  data-testid={`platform-console-public-api-saving-${tenant.id}`}
                >
                  <Spinner size="xs" />
                  Saving access…
                </MutedText>
              ) : null}
              {entitlementError ? (
                <ErrorAlert data-testid={`platform-console-public-api-error-${tenant.id}`}>
                  {entitlementError}
                </ErrorAlert>
              ) : null}
            </div>
          ) : null}
        </Panel>

        {reissuingTenantId === tenant.id ? (
          <ReissueInvitationForm
            // Keyed so reopening it builds a new form rather than carrying a
            // previously typed address back.
            key={tenant.id}
            tenant={tenant}
            pending={reissuing}
            error={reissueError}
            onSubmit={onReissue}
            onCancel={() => {
              restoreFocusTo.current = `platform-console-reissue-start-${tenant.id}`;
              setReissuingTenantId(null);
              onReissueClosed();
            }}
          />
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              icon="paper-plane-tilt"
              onClick={() => {
                onReissueClosed();
                setReissuingTenantId(tenant.id);
              }}
              data-testid={`platform-console-reissue-start-${tenant.id}`}
            >
              Reissue invitation
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <Panel headingLevel={2} title="Tenant details" data-testid="platform-console-tenant-details">
      {body}
    </Panel>
  );
}
